import styled from '@emotion/styled';
import { MATCH_STATUSES } from 'domain/entities/Match/Match';
import PointIcon from 'ui/images/point.svg';
import BsPeopleIcon from 'ui/images/bs_people.svg';
import NoImage from 'ui/images/no_image_case.png';
import { mqUser } from 'utils/responsive';
import { ColorLabel } from 'ui/components/elements/ColorLabel/ColorLabel';
import dayjs from 'dayjs';
import { IPublicBuyer } from 'domain/entities/Buyer/Buyer';

interface Props {
    buyer: IPublicBuyer;
    showCompanyTypeLabel?: boolean;
    onClick: () => void;
}

const Card = styled.div`
    width: calc(50% - 10px);
    background: white;
    padding: 20px;
    border-radius: 10px;
    box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.1);
    display: flex;
    gap: 20px;
    cursor: pointer;

    &:hover {
        box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.2);
    }

    ${mqUser('SP')} {
        width: 100%;
        flex-direction: column;
        gap: 12px;
    }
`;

const ImgContainer = styled.div`
    width: 132px;
    height: 132px;
    border-radius: 8px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
    position: relative;

    img {
        width: 100%;
        height: 100%;
        max-width: none;
        max-height: none;
        border-radius: 8px;
        object-fit: cover;
        display: block;
    }

    ${mqUser('SP')} {
        width: 100%;
        height: auto;
        aspect-ratio: 1 / 1;
    }
`;

const Detail = styled.div`
    display: flex;
    gap: 8px;
    flex-direction: column;
`;

const Label = styled.div`
    padding: 5px;
    gap: 4px;
    height: fit-content;
    border-radius: 4px;
    background-color: #f3f3f3;
    display: flex;
    align-items: center;
    font-size: 12px;
    color: #222222;
    font-weight: 500;

    img {
        flex-shrink: 0;
    }

    ${mqUser('SP')} {
        word-break: break-word;
        white-space: normal;
        flex-wrap: wrap;
    }
`;

const LabelContainer = styled.div`
    display: flex;
    gap: 5px;
    flex-wrap: wrap;

    ${mqUser('SP')} {
        gap: 6px;
    }
`;

const IndustryLabel = styled.div`
    font-weight: 700;
    font-size: 14px;
    line-height: 21px;
    letter-spacing: 10%;
`;

const ImageLabel = styled.div`
    position: absolute;
    bottom: 50%;
    left: 50%;
    transform: translate(-50%, 50%);
    border-radius: 30px;
    padding: 5px;
    font-weight: 700;
    font-size: 12px;
    line-height: 14.4px;
    letter-spacing: 0%;
    white-space: nowrap;
    color: white;
    background-color: #ee793f;
`;

const CompanyLabel = styled.div`
    position: absolute;
    top: 0px;
    left: 0px;
    border-radius: 8px 0 8px 0;
    padding: 5px;
    font-weight: 700;
    font-size: 12px;
    line-height: 14.4px;
    letter-spacing: 0%;
    white-space: nowrap;
    color: white;
    background-color: var(--main-color-blue);
`;

const CreatedAt = styled.div`
    font-size: 12px;
    color: #222222;
    font-weight: 500;
`;

const BottomContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: auto;
`;

const UserTypeLabel = styled.div`
    position: absolute;
    bottom: 50%;
    left: 50%;
    transform: translate(-50%, 50%);
    white-space: nowrap;
`;

export const PublicBuyerCardHorizontally: React.FC<Props> = ({ buyer, showCompanyTypeLabel = false, onClick }) => {
    return (
        <Card onClick={onClick}>
            <ImgContainer>
                <img src={NoImage} alt={'ロゴ画像'} />
                {showCompanyTypeLabel && (
                    <UserTypeLabel>
                        <ColorLabel color="#fff" backgroundColor="var(--primary)">
                            買い手企業
                        </ColorLabel>
                    </UserTypeLabel>
                )}
                {buyer?.match_status && (
                    <ImageLabel>
                        {buyer.match_status === MATCH_STATUSES.MATCH_STATUS_WAITING_ACCEPT && 'オファー済み'}
                        {buyer.match_status === MATCH_STATUSES.MATCH_STATUS_CONNECTED && 'マッチング済み'}
                        {buyer.match_status === MATCH_STATUSES.MATCH_STATUS_EXPIRED && 'オファー期限切れ'}
                        {buyer.match_status === MATCH_STATUSES.MATCH_STATUS_DECLINED && 'オファー辞退'}
                    </ImageLabel>
                )}
                {buyer?.label && <CompanyLabel>{buyer.label}</CompanyLabel>}
            </ImgContainer>
            <Detail>
                <IndustryLabel> 買収希望金額：{buyer.target_company_purchase_amount || '未設定'}</IndustryLabel>

                <LabelContainer>
                    <Label>
                        <img src={BsPeopleIcon} alt="人アイコン" />
                        {`買収希望案件業種 | ${
                            buyer.target_company_industries ? buyer.target_company_industries[0] + ' +他' : '未設定'
                        }`}
                    </Label>
                    <Label>
                        <img src={PointIcon} alt="ピンアイコン" />
                        {`買収希望案件エリア | ${buyer.area ? buyer.area[0] + ' +他' : '未設定'}`}
                    </Label>
                    <Label>
                        <img src={BsPeopleIcon} alt="人アイコン" />
                        {`買収希望案件企業の従業員数 | ${
                            buyer.target_company_employee_size ? buyer.target_company_employee_size : '未設定'
                        }`}
                    </Label>
                </LabelContainer>
                <BottomContainer>
                    <div>
                        {buyer.is_sole_proprietor && (
                            <ColorLabel color="#008000" backgroundColor="#d3ffa8">
                                個人事業主
                            </ColorLabel>
                        )}
                    </div>
                    <CreatedAt>登録日：{dayjs(buyer.created_at).format('YYYY/MM/DD')}</CreatedAt>
                </BottomContainer>
            </Detail>
        </Card>
    );
};
